import {
    Box,
    Button,
    capitalize,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {GridColDef, GridRowId} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../../data";
import {useT} from "../../../i18n";
import SearchField from "../../SearchField";
import DataTable from "../../data/DataTable";
import {useGetApi} from "../../../client";
import ErstelleAnbauteil from "../../../pages/fuhrpark/anbauteil/ErstelleAnbauteil";
import AnbauteilDetails from "../../../pages/fuhrpark/anbauteil/AnbauteilDetails";
import EditIcon from "@mui/icons-material/Edit";
import AddBoxIcon from "@mui/icons-material/AddBox";
import WeiseAnbauteilFahrzeugZu from "../../../pages/fuhrpark/fahrzeug/WeiseAnbauteilFahrzeugZu";
import DeleteAnbauteilButton from "./DeleteAnbauteilButton";
import useCustomFilters from "../../data/useCustomFilters";

export type AnbauteilFilter = {
    fahrzeugId: string;
    search?: string;
    filtered?: string;
};

export type Anbauteil = {
    id: string;
    geraeteBezeichnung: string;
};

export type Props = Omit<DataRequest<AnbauteilFilter>, "filter"> &
    Partial<Pick<DataRequest<AnbauteilFilter>, "filter">>;

const tableName: string = "anbauteile"

export default function AnbauteilDataTable({...input}: Props) {
    const {id: fahrzeugId = ""} = useParams<{
        id: string;
    }>();

    const request = useDataRequest<AnbauteilFilter>({
        filter: {fahrzeugId: fahrzeugId},
        ...input,
    });

    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp request={request} filteredStateFilter={filteredState}/>
            <AnbauteilResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<AnbauteilFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AnbauteilFilter>({
        tableName: tableName,
        defaultFilter: {
            fahrzeugId: request.filter.fahrzeugId,
            search: "",
        }
    });
    const [open, setOpen] = useState(false);
    const {t: translation} = useT("fuhrpark");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        setFilter({
            fahrzeugId: currentCustomFilter.fahrzeugId,
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={handleClickOpen}
                    >
                        {capitalize(translation("create-attachment-part"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={handleClickOpen}
                    >
                        {capitalize(translation("create-attachment-part"))}
                    </Button>
                    <Dialog
                        title={capitalize(translation("new-attachment-part"))}
                        open={open}
                        onClose={handleClose}
                    >
                        <DialogContent>
                            <ErstelleAnbauteil onClose={handleClose}/>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </Box>
    );
}

function AnbauteilResults({
                              request,
                              setFilteredState,
                          }: {
    request: DataRequestState<AnbauteilFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<AnbauteilFilter>;
    setFilteredState: (value: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [openAssign, setOpenAssign] = useState(false);
    const [anbauteilId, setAnbauteilId] = useState<string>("");
    const getApi = useGetApi();
    const {t} = useT("fuhrpark");

    const handleClickOpenAssign = (anbauteilRowId: GridRowId) => {
        setOpenAssign(true);
        setAnbauteilId(anbauteilRowId.toString());
    };

    const handleClickOpen = (anbauteilRowId: GridRowId) => {
        setOpen(true);
        setAnbauteilId(anbauteilRowId.toString());
    };

    const handleClose = () => {
        setOpen(false);
        setOpenAssign(false);
    };

    const ladeAnbauteile: DataLoader<AnbauteilFilter, Anbauteil> = useCallback(
        async (params) =>
            (await getApi()).anbauteile.sucheUnbenutzeFahrzeugAnbauteile({
                ...params,
            }),
        [getApi]
    );

    return (
        <>
            <DataTable
                columns={useColumns(handleClickOpen, handleClickOpenAssign)}
                request={request}
                queryKey={[tableName]}
                loadData={ladeAnbauteile}
                name={tableName}
                setFiltered={setFilteredState}
            />
            <Dialog
                title={t("new-attachment-part")}
                open={open}
                onClose={handleClose}
            >
                <DialogContent>
                    <AnbauteilDetails anbauteilId={anbauteilId} onClose={handleClose}/>
                </DialogContent>
            </Dialog>
            <Dialog
                title={t("new-attachment-part")}
                open={openAssign}
                onClose={handleClose}
            >
                <DialogContent>
                    <WeiseAnbauteilFahrzeugZu
                        anbauteilId={anbauteilId}
                        onClose={handleClose}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

function useColumns(
    handleDialogOpen: (anbauteilRowId: GridRowId) => void,
    handleAssignDialogOpen: (anbauteilRowId: GridRowId) => void
): Array<GridColDef<Anbauteil>> {
    const {t: translation} = useT("fuhrpark");

    return useMemo(
        () => [
            {
                field: "geraeteBezeichnung",
                headerName: capitalize(translation("attachment-part-title")),
                flex: 1,
            },
            {
                field: "editAnbauteil",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <IconButton onClick={() => handleDialogOpen(params.row.id)}>
                            <EditIcon/>
                        </IconButton>
                    </>
                ),
            },
            {
                field: "assignAnbauteil",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <IconButton onClick={() => handleAssignDialogOpen(params.row.id)}>
                            <AddBoxIcon/>
                        </IconButton>
                    </>
                ),
            },
            {
                field: "deleteAnbauteil",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <DeleteAnbauteilButton id={params.row.id}/>
                    </>
                ),
            },
        ],
        [translation, handleDialogOpen, handleAssignDialogOpen]
    );
}
